"use client";

/**
 * IndexedDB v2 — base de datos offline genérica para módulos del Agro.
 *
 * Diseño:
 *   - DB separada de la legacy `cacique-offline` v1 (`lib/offline-queue.ts`)
 *     para no chocar. Coexisten.
 *   - Stores:
 *       outbox  : mutaciones encoladas (FIFO, con reintentos exponenciales)
 *       cache   : registros por dominio (key = `${domain}:${id}`)
 *       id_map  : mapping de id temporal → real (cuando una creación offline se
 *                 sincroniza, los registros que la referencian deben rebindearse)
 *       meta    : metadatos por dominio (lastSyncAt, etc.)
 *
 *   - Sin dependencias externas (raw IDB, mismo estilo que la legacy).
 */

// v3: bump por bugs de cache que dejaban entries con campos parciales.
// El upgrade limpia el store `cache` (data potencialmente corrupta).
// Outbox/idMap/meta se preservan (datos pendientes del usuario).
// v4: agrega el store `blobs` (fotos comprimidas del form pre-cosecha, subida-al-
// sync). PURAMENTE ADITIVO: los guards `if (!contains(...))` preservan TODOS los
// stores existentes (outbox/cache/idMap/meta con sus datos); solo se crea el
// nuevo. Ningún dato del usuario se toca al subir a v4.
const DB_NAME = "cacique-offline-v2";
const DB_VERSION = 4;

export const STORES = {
  outbox: "outbox",
  cache: "cache",
  idMap: "id_map",
  meta: "meta",
  blobs: "blobs",
} as const;

let dbPromise: Promise<IDBDatabase> | null = null;

export function getDB(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB no soportado"));
  }
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldVersion = event.oldVersion;

      // v3: limpiar el store `cache` (data potencialmente parcial/corrupta por
      // bugs previos). Outbox/idMap/meta se PRESERVAN (mutaciones pendientes del
      // usuario). Corre una sola vez por dispositivo al subir a v3.
      if (oldVersion > 0 && oldVersion < 3 && db.objectStoreNames.contains(STORES.cache)) {
        const tx = req.transaction;
        if (tx) {
          try {
            tx.objectStore(STORES.cache).clear();
          } catch {
            /* ignore */
          }
        }
      }

      if (!db.objectStoreNames.contains(STORES.outbox)) {
        const store = db.createObjectStore(STORES.outbox, {
          keyPath: "id",
          autoIncrement: true,
        });
        store.createIndex("status", "status", { unique: false });
        store.createIndex("createdAt", "createdAt", { unique: false });
        store.createIndex("nextAttemptAt", "nextAttemptAt", { unique: false });
        store.createIndex("kind", "kind", { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.cache)) {
        const store = db.createObjectStore(STORES.cache, { keyPath: "key" });
        store.createIndex("domain", "domain", { unique: false });
        store.createIndex("updatedAt", "updatedAt", { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.idMap)) {
        const store = db.createObjectStore(STORES.idMap, { keyPath: "tmpId" });
        store.createIndex("domain", "domain", { unique: false });
        store.createIndex("realId", "realId", { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.meta)) {
        db.createObjectStore(STORES.meta, { keyPath: "key" });
      }

      // v4: fotos comprimidas del form pre-cosecha (Blobs binarios, NO base64).
      // key = `${parcelaRef}:${slot}` (parcelaRef = id real o tmpId). El
      // foto-uploader las sube al reconectar y marca uploaded:true.
      if (!db.objectStoreNames.contains(STORES.blobs)) {
        const store = db.createObjectStore(STORES.blobs, { keyPath: "key" });
        store.createIndex("parcelaRef", "parcelaRef", { unique: false });
        store.createIndex("uploaded", "uploaded", { unique: false });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

// ── Helpers transaccionales tipados ──────────────────────────────────────

export type StoreName = (typeof STORES)[keyof typeof STORES];

export async function txReadWrite<T>(
  store: StoreName | StoreName[],
  fn: (
    tx: IDBTransaction,
    store: IDBObjectStore | IDBObjectStore[]
  ) => Promise<T> | T
): Promise<T> {
  const db = await getDB();
  return new Promise<T>((resolve, reject) => {
    const stores = Array.isArray(store) ? store : [store];
    const tx = db.transaction(stores, "readwrite");
    const objectStores = stores.map((s) => tx.objectStore(s));
    let result: T | undefined;
    Promise.resolve(
      fn(tx, Array.isArray(store) ? objectStores : objectStores[0])
    )
      .then((r) => {
        result = r;
      })
      .catch((e) => {
        try {
          tx.abort();
        } catch {
          /* ignore */
        }
        reject(e);
      });
    tx.oncomplete = () => resolve(result as T);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("tx aborted"));
  });
}

export async function txReadOnly<T>(
  store: StoreName | StoreName[],
  fn: (
    tx: IDBTransaction,
    store: IDBObjectStore | IDBObjectStore[]
  ) => Promise<T> | T
): Promise<T> {
  const db = await getDB();
  return new Promise<T>((resolve, reject) => {
    const stores = Array.isArray(store) ? store : [store];
    const tx = db.transaction(stores, "readonly");
    const objectStores = stores.map((s) => tx.objectStore(s));
    let result: T | undefined;
    Promise.resolve(
      fn(tx, Array.isArray(store) ? objectStores : objectStores[0])
    )
      .then((r) => {
        result = r;
      })
      .catch(reject);
    tx.oncomplete = () => resolve(result as T);
    tx.onerror = () => reject(tx.error);
  });
}

export function reqAsPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Pub/sub para reactividad UI ─────────────────────────────────────────

const listeners = new Map<string, Set<() => void>>();

export function notify(channel: string): void {
  const set = listeners.get(channel);
  if (!set) return;
  set.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore listener errors */
    }
  });
}

export function listen(channel: string, fn: () => void): () => void {
  let set = listeners.get(channel);
  if (!set) {
    set = new Set();
    listeners.set(channel, set);
  }
  set.add(fn);
  return () => {
    set?.delete(fn);
  };
}

export const CHANNELS = {
  outbox: "outbox-change",
  cache: "cache-change",
} as const;
